import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  useGridApiRef,
  type DataGridPremiumProps,
  type GridColDef,
  type GridInitialState,
  type GridSortModel,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import {
  backendNumericOperators,
  stringFilterOperators,
  stringFilterOperatorsWithEquals,
} from "@/utils/gridFilterOperators";
import { useFeatureFlags } from "@/context/FeatureFlags";
import { UiFeatureFlagNames } from "@/gql/graphql";
import { useSaveGridSnapshot } from "@/hooks/useSaveGridSnapshot";
import { useGridUrlState } from "@/hooks/useGridUrlState";
import { useViewportFit } from "@/hooks/useViewportFit";
import { mergeWith } from "lodash-es";

const ACTIONS_FIELD = "actions";

const isActionsColumn = (column: GridColDef): boolean =>
  column.type === "actions" || column.field === ACTIONS_FIELD;

// Persist layout only; filters and current page are intentionally excluded.
const reduceCachedState = (state: GridInitialState): GridInitialState => ({
  pinnedColumns: state.pinnedColumns,
  pagination: {
    paginationModel: {
      pageSize: state.pagination?.paginationModel?.pageSize,
    },
  },
  columns: state.columns,
  sorting: state.sorting,
});

type DataGridProps<R extends GridValidRowModel> = DataGridPremiumProps<R> & {
  /** localStorage key for caching layout (columns, sorting, pinning, page size). Omit to disable. */
  name?: string;
  /** Keep this grid's bottom scrollbar within the viewport. */
  fitToViewport?: boolean;
  /** Opt-in URL synchronization for filter and sort state. */
  urlState?: {
    enabled?: boolean;
    defaultSortModel: GridSortModel;
    /** Prefix for the URL keys when several URL-synced grids share a page. */
    namespace?: string;
  };
};

type DataGridBaseProps<R extends GridValidRowModel> = Omit<
  DataGridProps<R>,
  "urlState"
> & {
  initialStateOverride?: GridInitialState;
};

/**
 * `DataGridPremium` with project-wide rules: the actions column is pinned right,
 * non-hideable and hidden from the columns panel; backend-filtered grids (with a
 * `dataSource`) get default filter operators; and `name` enables state caching.
 */
const DataGridBase = <R extends GridValidRowModel>(
  props: DataGridBaseProps<R>
) => {
  const {
    name,
    fitToViewport = false,
    apiRef: apiRefProp,
    columns,
    initialState,
    initialStateOverride,
    slotProps,
    dataSource,
    getDetailPanelContent,
    onPaginationModelChange,
    ...rest
  } = props;

  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;
  const { canUseFeatureFlag } = useFeatureFlags();
  const canUseEqualsStringFilter = canUseFeatureFlag(
    UiFeatureFlagNames.UxUpdate
  );
  // Falsy name (undefined or "") means no caching, matching useSaveGridSnapshot.
  const cachingEnabled = Boolean(name);
  const cachedState = useSaveGridSnapshot(apiRef, name);
  const viewportFitRef = useViewportFit(fitToViewport);

  const actionFields = columns.filter(isActionsColumn).map((c) => c.field);
  const hasActions = actionFields.length > 0;
  const hasDetailPanel = getDetailPanelContent !== undefined;
  const isBackendFiltered = dataSource !== undefined;

  // Structural columns kept out of the columns-management panel.
  const nonTogglableFields = hasDetailPanel
    ? [...actionFields, GRID_DETAIL_PANEL_TOGGLE_FIELD]
    : actionFields;

  const resolvedColumns = columns.map((column) => {
    let next = column;

    // Don't override operators a column set itself.
    if (
      isBackendFiltered &&
      next.filterable !== false &&
      next.filterOperators === undefined &&
      !isActionsColumn(next)
    ) {
      if (next.type === "number") {
        next = { ...next, filterOperators: backendNumericOperators };
      } else if (next.type === undefined || next.type === "string") {
        next = {
          ...next,
          filterOperators: canUseEqualsStringFilter
            ? stringFilterOperatorsWithEquals
            : stringFilterOperators,
        };
      }
    }

    if (isActionsColumn(next)) {
      next = { ...next, hideable: false };
    }

    return next;
  });

  // Caller `initialState` provides the defaults; cached layout is merged on top.
  const baseInitialState =
    cachingEnabled && cachedState
      ? mergeWith(
          {},
          initialState,
          reduceCachedState(cachedState),
          (_value, sourceValue) =>
            Array.isArray(sourceValue) ? sourceValue : undefined
        )
      : initialState;
  const stateWithOverrides = initialStateOverride
    ? mergeWith(
        {},
        baseInitialState,
        initialStateOverride,
        (_value, sourceValue) =>
          Array.isArray(sourceValue) ? sourceValue : undefined
      )
    : baseInitialState;

  const resolvedInitialState = hasActions
    ? {
        ...stateWithOverrides,
        pinnedColumns: {
          ...stateWithOverrides?.pinnedColumns,
          right: [
            ...(stateWithOverrides?.pinnedColumns?.right ?? []).filter(
              (field) => !actionFields.includes(field)
            ),
            ...actionFields,
          ],
        },
      }
    : stateWithOverrides;

  const callerGetTogglableColumns =
    slotProps?.columnsManagement?.getTogglableColumns;

  // Subtract only structural columns; leave the rest to MUI's default.
  const getTogglableColumns = (gridColumns: GridColDef[]): string[] => {
    const base = callerGetTogglableColumns
      ? callerGetTogglableColumns(gridColumns)
      : gridColumns.map((column) => column.field);
    return base.filter((field) => !nonTogglableFields.includes(field));
  };

  const resolvedSlotProps =
    nonTogglableFields.length > 0
      ? {
          ...slotProps,
          columnsManagement: {
            ...slotProps?.columnsManagement,
            getTogglableColumns,
          },
        }
      : slotProps;

  const handlePaginationModelChange: NonNullable<
    DataGridPremiumProps<R>["onPaginationModelChange"]
  > = (model, details) => {
    onPaginationModelChange?.(model, details);
    const api = apiRef.current;
    if (!hasDetailPanel || !api) {
      return;
    }
    const expanded = api.getExpandedDetailPanels();
    if (expanded.size === 0) {
      return;
    }
    // GridDetailPanels only rebuilds when expandedRowIds identity changes, not on page change.
    api.setExpandedDetailPanels(new Set(expanded));
  };

  const grid = (
    <DataGridPremium
      apiRef={apiRef}
      columns={resolvedColumns}
      initialState={resolvedInitialState}
      slotProps={resolvedSlotProps}
      dataSource={dataSource}
      getDetailPanelContent={getDetailPanelContent}
      {...rest}
      onPaginationModelChange={handlePaginationModelChange}
    />
  );

  if (!fitToViewport) {
    return grid;
  }

  return (
    <div
      ref={viewportFitRef}
      style={{
        height: "var(--data-grid-viewport-fit-height, 500px)",
        minHeight: 0,
      }}
    >
      {grid}
    </div>
  );
};

const UrlStateDataGrid = <R extends GridValidRowModel>(
  props: DataGridProps<R>
) => {
  const {
    urlState,
    apiRef: apiRefProp,
    initialState,
    onFilterModelChange: callerOnFilterModelChange,
    onSortModelChange: callerOnSortModelChange,
    ...rest
  } = props;
  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? internalApiRef;
  const gridUrlState = useGridUrlState({
    apiRef,
    defaultSortModel: urlState?.defaultSortModel ?? [],
    namespace: urlState?.namespace,
  });

  const onFilterModelChange: NonNullable<
    DataGridPremiumProps["onFilterModelChange"]
  > = (filterModel, details) => {
    gridUrlState.onFilterModelChange(filterModel);
    callerOnFilterModelChange?.(filterModel, details);
  };

  const onSortModelChange: NonNullable<
    DataGridPremiumProps["onSortModelChange"]
  > = (sortModel, details) => {
    gridUrlState.onSortModelChange(sortModel);
    callerOnSortModelChange?.(sortModel, details);
  };

  return (
    <DataGridBase
      {...rest}
      apiRef={apiRef}
      initialState={initialState}
      initialStateOverride={gridUrlState.initialState}
      onFilterModelChange={onFilterModelChange}
      onSortModelChange={onSortModelChange}
    />
  );
};

const DataGrid = <R extends GridValidRowModel>(props: DataGridProps<R>) => {
  const { urlState } = props;

  if (urlState?.enabled !== true) {
    const baseProps = { ...props };
    delete baseProps.urlState;
    return <DataGridBase {...baseProps} />;
  }

  return <UrlStateDataGrid {...props} />;
};

export default DataGrid;
export type { DataGridProps };
